/**
 * @module app.cm
 */

import { autoinject, singleton, Disposable, computedFrom, PLATFORM } from 'aurelia-framework';
import { Router, RouterConfiguration, RouteConfig, NavModel } from 'aurelia-router';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { apiCall } from 'app/shared/lib/essette-api';
import { EventAggregator } from 'aurelia-event-aggregator';
import { IApiSettings, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { IAppRouteConfig } from 'app/shared/config/router-types';
import { IQueueParameters, IQueueNavigationBinding } from 'app/core/models/essette-interfaces';
import { guid } from 'app/shared/lib/guid';
import * as CM from 'app/cm/models/essette-api';

export interface ICasePageParameters extends Partial<IQueueParameters> {
    caseId: string;
    queueTypeCode?: 'CM.Routing';
}

@autoinject()
export class CasePage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected case: CM.Case = {};
    protected caseSettings: IApiSettings = {};
    protected memberCases: CM.Case[] = [];
    protected memberHouseholdCases: CM.HouseholdCase[] = [];
    protected currentRoute: string = '';
    protected queueBinding: IQueueNavigationBinding | null = null;

    protected programGroups: CM.ProgramGroup[] = [];
    protected acuities: CM.Acuity[] = [];
    protected intensities: CM.Intensity[] = [];

    public hasCreateAdditional: boolean = false;
    protected hasHouseholdCaseRoute: boolean = false;

    protected id: guid = guid();

    @computedFrom('case', 'case.id')
    private get caseId(): string | null {
        return this.case && this.case.id || null;
    }

    protected caseNavTabIds: string[] = [];

    ////****************** Element References ***************************************////


    ////****************** Event Subscriptions **************************************////
    private caseEditedEvent: Disposable | null = null;  // Add subscription in attached(). dispose of in detached()
    private navigationCompletedEvent: Disposable | null = null;


    ////****************** Lifecycle events (in order of execution) *****************////
    //TODO: Get rid of event aggregator if possible
    constructor(
        private readonly caseService: CaseService,
        @singleton(true) private readonly context: CasePageContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
    }

    protected async activate(params: ICasePageParameters, routeConfig: RouteConfig): Promise<void> {
        if(params.caseId && params.caseId !== this.case.id) {

            this.checkPermissions();

            // load case and settings onto context at the same time
            await Promise.all([
                this.loadCase(params.caseId),
                this.loadSettings(),
                this.loadLookups()
            ]);

            if(this.case.member?.id) {
                this.loadMemberCases(this.case.member.id);
            }

            if(params.queueTypeCode) {
                this.queueBinding = {
                    id: params.caseId,
                    queueTypeCode: params.queueTypeCode,
                    queueId: params.queueId,
                    queueTargetId: params.queueTargetId,
                    queueAction: params.queueAction?.toLowerCase(),
                    sourceCode: params.sourceCode,
                    documentsConfig: {
                        serviceRootUrl: 'cm/cases',
                        targetType: 'CM.Case'
                    }
                } as IQueueNavigationBinding;
            } else if(this.context.queueBinding?.id !== this.caseId) {
                this.queueBinding = null;
            }

            if(routeConfig.navModel) {
                routeConfig.navModel.setTitle(`Case #${this.case.number}`);
            }
        }
    }

    protected attached(): void {
        // store queue binding data on context
        if(this.queueBinding) this.context.queueBinding = this.queueBinding;

        this.caseEditedEvent = this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => {
            if(e.case && e.case.id === this.caseId) {
                this.reload();
            }
        });

        this.navigationCompletedEvent = this.events.subscribe('router:navigation:complete', () => this.navigationComplete());
        this.navigationComplete();
    }

    protected detached(): void {
        if(this.caseEditedEvent) { this.caseEditedEvent.dispose(); this.caseEditedEvent = null; }
        if(this.navigationCompletedEvent) { this.navigationCompletedEvent.dispose(); this.navigationCompletedEvent = null; }
    }


    ////****************** Events ***************************************************////
    protected navigationComplete(): void {
        if(!this.queueBinding && this.context.queueBinding?.id === this.caseId) {
            // if navigating to assessments and context has queue binding data, restore that data from context
            this.queueBinding = this.context.queueBinding;
        } else {
            // otherwise, update context
            this.context.queueBinding = this.queueBinding;
        }
        const routeConfig = this.router.currentInstruction.config;
        const route = Array.isArray(routeConfig.route) ? routeConfig.route[0] : routeConfig.route;
        const tab = routeConfig.settings && (routeConfig.settings.tab as string) || '';
        if(routeConfig && (routeConfig.nav || tab)) {
            this.currentRoute = `/${tab || route}`;
        }
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCreateAdditional = this.accountContext.hasPermission('CM.Case.Duty.Additional');

        this.hasHouseholdCaseRoute = this.accountContext.hasRouteAccess('cm.household-case');
    }

    protected async configureRouter(config: RouterConfiguration, router: Router): Promise<void> {
        await this.accountContext.load();
        const routes: IAppRouteConfig[] = [
            { route: '', redirect: 'careplan' },
            // Tab Navigation
            {
                route: 'assessments', name: 'cm.case.assessments', moduleId: PLATFORM.moduleName('app/cm/pages/case/case-assessments-page'), title: 'cm.case.assessments', nav: true,
                settings: { icon: 'ui:icons.assessment', isVisible: this.accountContext.hasRouteAccess('cm.case.assessments'), topic: 'cm.case.assessments' }
            },
            {
                route: 'careteam', name: 'cm.case.careteam', moduleId: PLATFORM.moduleName('app/cm/pages/case/care-team/care-team-page'), title: 'cm.case.careteam', nav: true,
                settings: { icon: 'ui:icons.users', isVisible: this.accountContext.hasRouteAccess('cm.case.careteam'), topic: 'cm.case.careteam' }
            },
            {
                route: 'careplan', name: 'cm.case.careplan', moduleId: PLATFORM.moduleName('app/cm/pages/case/care-plan/care-plan-page'), title: 'cm.case.careplan', nav: true,
                settings: { icon: 'ui:icons.carePlan', isVisible: this.accountContext.hasRouteAccess('cm.case.careplan'), topic: 'cm.case.careplan' }
            },
            {
                route: 'tasks', name: 'cm.case.tasks', moduleId: PLATFORM.moduleName('app/cm/pages/case/tasks/case-tasks-page'), title: 'cm.case.tasks', nav: true,
                settings: { icon: 'ui:icons.tasks', isVisible: this.accountContext.hasRouteAccess('cm.case.tasks'), topic: 'cm.case.tasks' }
            },
            {
                route: 'notes', name: 'cm.case.notes', moduleId: PLATFORM.moduleName('app/cm/pages/case/case-notes-page'), title: 'cm.case.notes', nav: true,
                settings: { icon: 'ui:icons.note', isVisible: this.accountContext.hasRouteAccess('cm.case.notes'), topic: 'cm.case.notes' }
            },
            {
                route: 'correspondence', name: 'cm.case.correspondence', moduleId: PLATFORM.moduleName('app/cm/pages/case/case-correspondence-page'), title: 'cm.case.correspondence', nav: true,
                settings: { icon: 'ui:icons.correspondence.general', isVisible: this.accountContext.hasRouteAccess('cm.case.correspondence'), topic: 'cm.case.correspondence' }
            },
            {
                route: 'documents', name: 'cm.case.documents', moduleId: PLATFORM.moduleName('app/cm/pages/case/case-documents-page'), title: 'cm.case.documents', nav: true,
                settings: { icon: 'ui:icons.document', isVisible: this.accountContext.hasRouteAccess('cm.case.documents'), topic: 'cm.case.documents' }
            },
            {
                route: 'diagnoses', name: 'cm.case.member-diagnoses', moduleId: PLATFORM.moduleName('app/cm/pages/case/member-diagnoses-page'), title: 'cm.case.member-diagnoses', nav: true,
                settings: { icon: 'ui:icons.diagnoses', isVisible: this.accountContext.hasRouteAccess('cm.case.member-diagnoses'), topic: 'cm.case.diagnoses' }
            },
            {
                route: 'status', name: 'cm.case.status', moduleId: PLATFORM.moduleName('app/cm/pages/case/case-status-page'), title: 'cm.case.status', nav: true,
                settings: { icon: 'ui:icons.history', isVisible: this.accountContext.hasRouteAccess('cm.case.status'), topic: 'cm.case.status' }
            },
            {
                route: 'routing', name: 'cm.case.routing', moduleId: PLATFORM.moduleName('app/cm/pages/case/case-routing-page'), title: 'cm.case.routing', nav: true,
                settings: { icon: 'ui:icons.send', isVisible: this.accountContext.hasRouteAccess('cm.case.routing'), topic: 'cm.case.routing' }
            },
            {
                route: 'services', name: 'cm.case.services', moduleId: PLATFORM.moduleName('app/cm/pages/case/case-services-page'), title: 'cm.case.services', nav: true,
                settings: { icon: 'ui:icons.service', isVisible: this.accountContext.hasRouteAccess('cm.case.services'), topic: 'cm.case.services' }
            },
            // Care Plan Detail Routes
            {
                route: 'tasks/:taskId', name: 'cm.case.task', moduleId: PLATFORM.moduleName('app/cm/pages/case/tasks/edit-case-task-page'), title: 'cm.case.edit.task',
                settings: { tab: 'tasks', isVisible: true, topic: 'cm.task' }
            },
            {
                route: 'careplan/goals/:goalId', name: 'cm.case.careplan.goals.edit', moduleId: PLATFORM.moduleName('app/cm/pages/case/care-plan/edit-goal-page'), title: 'cm.case.edit.goal',
                settings: { tab: 'careplan', isVisible: true, topic: 'cm.careplan.goal' }
            },
            {
                route: 'careplan/goals/:goalId/interventions/:taskId', name: 'cm.case.careplan.interventions.edit', moduleId: PLATFORM.moduleName('app/cm/pages/case/care-plan/edit-intervention-page'), title: 'cm.case.edit.intervention',
                settings: { tab: 'careplan', isVisible: true, topic: 'cm.careplan.intervention' }
            },
            {
                route: 'careplan/goals/:goalId/barriers/:barrierId', name: 'cm.case.careplan.barriers.edit', moduleId: PLATFORM.moduleName('app/cm/pages/case/care-plan/edit-barrier-page'), title: 'cm.case.edit.barrier',
                settings: { tab: 'careplan', isVisible: true, topic: 'cm.careplan.barrier' }
            },
            {
                route: 'careplan/goals/:goalId/outcomes/:outcomeId', name: 'cm.case.careplan.outcomes.edit', moduleId: PLATFORM.moduleName('app/cm/pages/case/care-plan/edit-outcome-page'), title: 'cm.case.edit.outcome',
                settings: { tab: 'careplan', isVisible: true, topic: 'cm.careplan.outcome' }
            }
        ];
        config.map(routes);

        config.mapUnknownRoutes('app/shared/components/not-found');

        this.router = router;
    }

    protected caseNavigationTabs(): Array<NavModel> {
        const tabs = this.router.navigation.filter(tab => tab.config.settings.isVisible);
        this.caseNavTabIds.splice(0);
        tabs.forEach((tab) => { this.caseNavTabIds.push(guid()); });
        return tabs;
    }

    protected generateTabLink(targetCaseId: string, targetRoute: string): string {
        const parentUrl = this.router.parent.generate('cm.case', { caseId: targetCaseId });
        return parentUrl + targetRoute;
    }

    protected generateHouseholdCaseTabLink(targetHouseholdCaseId: string, targetRoute: string): void {
        const parentUrl = this.router.parent.generate('cm.household-case', { householdCaseId: targetHouseholdCaseId });
        window.location.href = parentUrl + targetRoute;
    }

    protected getQueueNavigationView(): string | undefined {
        if(!this.queueBinding) return;
        const views = this.env.getDynamicViewsByKey('Core.QueueType', this.queueBinding.queueTypeCode);
        return views[0]?.url;
    }

    private async reload(): Promise<void> {
        if(this.caseId) {
            await this.loadCase(this.caseId);
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadSettings(): Promise<void> {
        try {
            this.caseSettings = await this.context.loadSettings();
        } catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadCase(caseId: string): Promise<void> {
        const includes = [
            'acuity',
            'assignedTo',
            'custom',
            'custom.target',
            'custom.target.definitions',
            'intensity',
            'member',
            'member.identifier',
            'member.language.reads.culture', // for correspondence
            'member.gender',
            'program',
            'program.group',
            'status',
            'status.reason'
        ];

        const fields = [
            'acuity.description',
            'assignedTo.description',
            'custom.lookup.*',
            'custom.target.definitions.description',
            'custom.target.definitions.property.field',
            'custom.target.definitions.property.type',
            'custom.text.*',
            'householdCase.id',
            'intensity.description',
            'isCheckedOut',
            'isClosed',
            'member.birthDate',
            'member.description',
            'member.gender.designation.identity',
            'member.identifier.external',
            'member.name.first',
            'member.name.full',
            'member.name.last',
            'member.name.middle',
            'metadata.created.timestamp',
            'metadata.rollupModified.timestamp',
            'metadata.statusChanged.timestamp',
            'number',
            'program.description',
            'program.group.code',
            'program.group.description',
            'program.isSystem',
            'status.description',
            'status.reason.description'
        ];

        try {
            this.state.beginLoad();
            this.case = await this.context.loadCase(caseId, includes, fields);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMemberCases(memberId: string): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = [
            'assignedTo',
            'program',
            'status'
        ];

        const fields = [
            'assignedTo.description',
            'metadata.created.timestamp',
            'number',
            'program.code',
            'program.description',
            'status.description',
            'status.flag.isOpen'
        ]

        if(this.hasHouseholdCaseRoute) {
            const householdCaseIncludes = [
                'householdCase',
                'householdCase.assignedTo',
                'householdCase.program',
                'householdCase.status'
            ];

            const householdCaseFields = [
                'householdCase.assignedTo.description',
                'householdCase.metadata.created.timestamp',
                'householdCase.number',
                'householdCase.program.code',
                'householdCase.program.description',
                'householdCase.status.description'
            ];

            includes.push(...householdCaseIncludes);
            fields.push(...householdCaseFields);
        }

        try {
            const response = await this.caseService.loadCasesByMember(memberId, options, includes, fields);
            this.memberCases = response.results;

            if(this.hasHouseholdCaseRoute) {
                this.memberHouseholdCases = [];
                const uniqueHouseholdIds = new Set();
                this.memberCases.forEach(memberCase => {
                    if(memberCase.householdCase && !uniqueHouseholdIds.has(memberCase.householdCase.id)) {
                        uniqueHouseholdIds.add(memberCase.householdCase.id);
                        this.memberHouseholdCases.push(memberCase.householdCase);
                    }
                });
            }
        }
        catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            const options: Partial<IQueryLoadOptions> = {
                all: true
            };

            const acuitiesFields = ['description', 'summary'];
            const intensitiesFields = ['description', 'summary'];
            const programGroupsIncludes = ['programs'];
            const programGroupsFields = ['code', 'description', 'isSystem', 'programs.code', 'programs.description'];

            const [acuities, intensities, programGroups] = await Promise.all([
                this.caseService.loadAcuities(options, undefined, acuitiesFields),
                this.caseService.loadIntensities(options, undefined, intensitiesFields),
                this.caseService.loadProgramGroups(options, programGroupsIncludes, true, false, false, programGroupsFields)
            ]);

            this.acuities = acuities.results;
            this.intensities = intensities.results;
            this.programGroups = programGroups.results;
        }
        catch(error) {
            throw error;
        }
    }
}

